// The SSM adapter: canonical types in, vendor body out, normalized result back.
//
// It owns no transport of its own (see core/transport.ts) and no business
// logic. Phase 1 implements the three write operations; getUnit arrives with
// phase 3, when ambiguous move-ins start being verified automatically. The
// capability set lists only what is actually implemented — declaring a
// capability we cannot perform is the exact bug capabilities exist to prevent.

import { checkSsmSuccessEnvelope, classifySsmFailure } from '../../classify';
import type { VendorFailureSignal } from '../../classify';
import type { PmsError } from '../../errors';
import type { PmsTransport, PmsTransportResult } from '../../transport';
import type {
  EnsureTenantInput,
  EnsureTenantOutput,
  MoveInInput,
  MoveInOutput,
  PmsAdapter,
  PmsCtx,
  PmsOp,
  PmsOpResult,
  PmsRequestEnvelope,
  RecordPaymentInput,
  RecordPaymentOutput,
  SsmFacilityConfig,
} from '../../types';
import { buildAddUpdateAccount, buildProcessMoveIn, buildProcessPayment } from './map';
import { assertSafeSsmRequest } from './guard';

const CAPABILITIES: ReadonlySet<PmsOp> = new Set<PmsOp>([
  'ensureTenant',
  'moveIn',
  'recordPayment',
]);

export interface SsmAdapterDeps {
  transport: PmsTransport;
}

/** Envelope stand-in for failures that happen before a body can be built. */
function emptyEnvelope(op: PmsOp, vendorOp: string): PmsRequestEnvelope {
  return { op, vendorOp, path: '', body: null };
}

function missingConfig(op: PmsOp, vendorOp: string): PmsOpResult<never> {
  return {
    ok: false,
    request: emptyEnvelope(op, vendorOp),
    error: {
      class: 'VALIDATION',
      kind: 'ours',
      message: 'No SSM configuration for this facility.',
    },
  };
}

function toSignal(result: PmsTransportResult): VendorFailureSignal {
  if (result.kind === 'network') {
    return { kind: 'network', message: result.message, raw: result.raw };
  }
  if (result.kind === 'http') {
    return {
      kind: 'http',
      httpStatus: result.httpStatus,
      message: `SSM returned HTTP ${result.httpStatus}.`,
      raw: result.body,
    };
  }
  return { kind: 'application', message: 'Unexpected transport result.' };
}

const SHADOW_ERROR: PmsError = {
  class: 'SKIPPED',
  kind: 'ours',
  message: 'Shadow mode: request was built and inspected, but not sent.',
};

/**
 * Shared plumbing: send the envelope, then turn whatever came back into a
 * result. `extract` only runs on a genuine success.
 */
async function send<T>(
  transport: PmsTransport,
  request: PmsRequestEnvelope,
  extract: (body: unknown) => T,
): Promise<PmsOpResult<T>> {
  // Second guard pass, immediately before anything could leave the process.
  assertSafeSsmRequest(request);

  const result = await transport.call(request);

  if (result.kind === 'shadow') {
    return { ok: false, request, error: SHADOW_ERROR };
  }

  if (result.kind !== 'ok') {
    return { ok: false, request, error: classifySsmFailure(toSignal(result), request.op) };
  }

  const envelopeError = checkSsmSuccessEnvelope(request.op, result.body);
  if (envelopeError) {
    return { ok: false, request, error: envelopeError };
  }

  return { ok: true, request, data: extract(result.body) };
}

function readString(body: unknown, key: string): string | null {
  if (typeof body !== 'object' || body === null) return null;
  const value = (body as Record<string, unknown>)[key];
  if (value == null) return null;
  return String(value);
}

export function createSsmAdapter(deps: SsmAdapterDeps): PmsAdapter {
  const { transport } = deps;

  const config = (ctx: PmsCtx): SsmFacilityConfig | null => ctx.config.ssm || null;

  return {
    provider: 'ssm',
    capabilities: CAPABILITIES,

    ensureTenant(input: EnsureTenantInput, ctx: PmsCtx) {
      const cfg = config(ctx);
      if (!cfg) return Promise.resolve(missingConfig('ensureTenant', 'AddUpdateAccount'));

      return send<EnsureTenantOutput>(
        transport,
        buildAddUpdateAccount(input, cfg),
        (body) => ({
          externalTenantID: readString(body, 'TenantId') || '',
          accessCode: readString(body, 'AccessCode'),
        }),
      );
    },

    moveIn(input: MoveInInput, ctx: PmsCtx) {
      const cfg = config(ctx);
      if (!cfg) return Promise.resolve(missingConfig('moveIn', 'ProcessMoveIn'));

      return send<MoveInOutput>(transport, buildProcessMoveIn(input, cfg), (body) => ({
        externalLeaseID: readString(body, 'LeaseNumber') || '',
        receiptNumber: readString(body, 'ReceiptNumber'),
        accessCode: readString(body, 'AccessCode'),
      }));
    },

    recordPayment(input: RecordPaymentInput, ctx: PmsCtx) {
      const cfg = config(ctx);
      if (!cfg) return Promise.resolve(missingConfig('recordPayment', 'ProcessPayment'));

      return send<RecordPaymentOutput>(transport, buildProcessPayment(input, cfg), (body) => ({
        receiptNumber: readString(body, 'ReceiptNumber') || '',
      }));
    },

    // moveOut is intentionally absent: SSM's API has no move-out endpoint, so
    // callers check the capability and fall back to an admin task.
  };
}
